import PropTypes from 'prop-types';
import React from 'react';

import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  AT_MENTION_SEARCH_FINISHED_EVENT,
  AT_MENTION_SEARCH_STARTED_EVENT,
} from 'performance/processNotebookEvents';
import {DropdownSearchType, SEARCH_CORRELATION_ID_HEADER} from 'search/SearchConstants';
import {AttachDropdownItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import {SelectAttachment} from 'ui-library/attach-dropdown/AttachDropdownOptionsMenu';
import SearchDropdown, {SearchResultInfo} from 'ui-library/attach-dropdown/SearchDropdown';
import {DEFAULT_DROPDOWN_SEARCH_LIMIT} from 'ui-library/dropdowns/SearchDropdownConstants';
import * as net from 'util/Net';
import {ElementKeyboardEvent} from 'util/Types';

interface LinkSearchArgs {
  type: string;
  query: string;
  limit: number;
  batchLimit: number;
  shouldSearchBatches: string;
  fromFileId?: string;
  shouldSearchEntities?: string;
  entityLimit?: number;
  shouldSearchStorables?: string;
}

export interface LinkSearchDropdownProps {
  fileId?: string;
  shouldSearchEntities?: boolean;
  shouldSearchStorables?: boolean;
  query: string;
  selectAttachment: SelectAttachment;
  close?: () => void;
  defaultOptionSelected?: boolean;
  dropup?: boolean;
  emptyQueryMessage?: JSX.Element | string;
  style?: React.CSSProperties;
  width: string | number;
}

export function LinkSearchDefaultQueryMessage(): JSX.Element {
  return (
    <span>
      Search for a Benchling{' '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://help.benchling.com/hc/articles/9684279746701"
      >
        object
      </a>
      {' or '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://help.benchling.com/hc/articles/9684271414157"
      >
        collaborator
      </a>
      .
    </span>
  );
}

interface LinkDropdownSearchOptions {
  query: string;
  fileId?: string;
  shouldSearchEntities?: boolean;
  shouldSearchStorables?: boolean;
}

const SEARCH_ENDPOINT = '/1/api/files/link-search';

function _finishProfiling(fileId: string | undefined, results: Array<AttachDropdownItem>): void {
  logProfilerTimingEvent(
    AT_MENTION_SEARCH_FINISHED_EVENT,
    results.reduce(
      (acc, result) => {
        if (result.name) {
          acc.hasNameCount++;
        }
        if (result.creator) {
          acc.hasCreatorCount++;
        }
        return acc;
      },
      {hasNameCount: 0, hasCreatorCount: 0, resultsCount: results.length, fileId}
    )
  );
}

export function doLinkDropdownSearch(
  {query, fileId, shouldSearchEntities, shouldSearchStorables}: LinkDropdownSearchOptions,
  searchResultsCallback: ({query, results, errorMessage}: SearchResultInfo) => void
): void {
  const searchArgs: LinkSearchArgs = {
    type: 'file',
    query,
    limit: DEFAULT_DROPDOWN_SEARCH_LIMIT,
    batchLimit: DEFAULT_DROPDOWN_SEARCH_LIMIT,
    shouldSearchBatches: '1',
  };
  if (fileId) {
    searchArgs.fromFileId = fileId;
  }
  if (shouldSearchEntities) {
    searchArgs.shouldSearchEntities = '1';
    searchArgs.entityLimit = 5;
  }
  if (shouldSearchStorables) {
    searchArgs.shouldSearchStorables = '1';
  }
  logProfilerTimingEvent(AT_MENTION_SEARCH_STARTED_EVENT);
  net
    .getJSON(SEARCH_ENDPOINT, searchArgs)
    .done((results, status, xhr) => {
      const correlationId = xhr.getResponseHeader(SEARCH_CORRELATION_ID_HEADER);
      searchResultsCallback({
        query,
        limit: DEFAULT_DROPDOWN_SEARCH_LIMIT,
        results,
        errorMessage: null,
        correlationId,
      });
      _finishProfiling(fileId, results);
    })
    .fail((res) => {
      const errorMessage = notifications.getFriendlyError(res) || 'An error occurred.';
      searchResultsCallback({query, limit: DEFAULT_DROPDOWN_SEARCH_LIMIT, results: [], errorMessage});
    });
}

class LinkSearchDropdown extends React.Component<LinkSearchDropdownProps> {
  static propTypes = {
    fileId: PropTypes.string,
    shouldSearchEntities: PropTypes.bool,
    shouldSearchStorables: PropTypes.bool,
  };

  searchDropdownRef = React.createRef<SearchDropdown>();

  _doSearch = (
    query: string,
    searchResultsCallback: ({query, limit, results, errorMessage}: SearchResultInfo) => void
  ): void => {
    doLinkDropdownSearch(
      {
        query,
        fileId: this.props.fileId,
        shouldSearchEntities: this.props.shouldSearchEntities,
        shouldSearchStorables: this.props.shouldSearchStorables,
      },
      searchResultsCallback
    );
  };

  queryKeydownHandler(e: ElementKeyboardEvent): boolean {
    if (!this.searchDropdownRef.current) {
      return false;
    }
    return this.searchDropdownRef.current.queryKeydownHandler(e);
  }

  override render(): JSX.Element {
    return (
      <SearchDropdown
        ref={this.searchDropdownRef}
        doSearch={this._doSearch}
        profilerTrackingParams={{
          url: SEARCH_ENDPOINT,
          searchType: DropdownSearchType.AT_MENTION,
        }}
        {...this.props}
      />
    );
  }
}

export default LinkSearchDropdown;
